import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, X, Check, CheckCheck, Droplets, Utensils, Award, Users, Flame, TestTube, PlusCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const typeIcons = {
  meal_breakfast: Utensils,
  meal_lunch: Utensils,
  meal_dinner: Utensils,
  water_reminder: Droplets,
  streak_alert_push: Flame,
  badge: Award,
  friend_activity: Users,
  test: TestTube,
  weekly_summary: Bell,
};

const typeColors = {
  meal_breakfast: 'text-amber-400',
  meal_lunch: 'text-orange-400',
  meal_dinner: 'text-red-400',
  water_reminder: 'text-blue-400',
  streak_alert_push: 'text-orange-500',
  badge: 'text-yellow-400',
  friend_activity: 'text-purple-400',
  test: 'text-green-400',
  weekly_summary: 'text-indigo-400',
};

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export default function NotificationBell({ token }) {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const fetchUnread = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/push/unread`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.count || 0);
      }
    } catch (e) { /* silent */ }
  }, [token]);

  useEffect(() => {
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, [fetchUnread]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Request browser notification permission
  useEffect(() => {
    try {
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission().catch(() => {});
      }
    } catch (e) {
      // Notification API not supported on this device
    }
  }, []);

  // Show browser notification for new items
  useEffect(() => {
    if (notifications.length > 0 && 'Notification' in window && Notification.permission === 'granted') {
      const latest = notifications[0];
      if (latest && !latest._shown) {
        try {
          new Notification(latest.title, { body: latest.body, icon: '/app-icon.png' });
        } catch (e) {
          // Fallback: On Android Chrome, new Notification() is not supported
          console.log('Browser notification not available');
        }
        latest._shown = true;
      }
    }
  }, [notifications]);

  const markAllRead = async () => {
    try {
      await fetch(`${API}/push/mark-read`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: [] })
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (e) { /* silent */ }
  };

  const sendTestNotification = async () => {
    try {
      await fetch(`${API}/push/test`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      setTimeout(fetchUnread, 1000);
    } catch (e) { /* silent */ }
  };

  const quickLogMeal = async (notification) => {
    if (!notification.data?.meal_name) return;
    try {
      const mealType = notification.type.replace('meal_', '') || 'snack';
      const res = await fetch(`${API}/meals/quick-log`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          meal_name: notification.data.meal_name,
          meal_type: mealType,
          calories: notification.data.meal_calories || 0,
          protein: notification.data.meal_protein || 0,
          carbs: notification.data.meal_carbs || 0,
          fats: notification.data.meal_fats || 0,
          notification_id: notification.id
        })
      });
      if (res.ok) {
        const result = await res.json();
        setNotifications(prev => prev.map(n =>
          n.id === notification.id ? { ...n, read: true, _logged: true } : n
        ));
        setUnreadCount(prev => Math.max(0, prev - 1));
        if ('Notification' in window && Notification.permission === 'granted') {
          try {
            new Notification('Meal Logged!', { body: result.message, icon: '/app-icon.png' });
          } catch (e) { /* Android Chrome doesn't support new Notification() */ }
        }
      }
    } catch (e) { /* silent */ }
  };

  return (
    <div className="relative" ref={dropdownRef} data-testid="notification-bell">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-full bg-transparent hover:bg-transparent active:opacity-60 transition-opacity"
        data-testid="notification-bell-btn"
      >
        <Bell className="w-5 h-5 text-[#FF6600]" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-orange-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold animate-pulse" data-testid="notification-badge">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-12 w-80 sm:w-96 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl z-50 overflow-hidden" data-testid="notification-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
            <h3 className="font-semibold text-white text-sm">{t('notif.title')}</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button onClick={markAllRead} className="text-xs text-orange-400 hover:text-orange-300 flex items-center gap-1" data-testid="mark-all-read-btn">
                  <CheckCheck className="w-3.5 h-3.5" /> {t('notif.markAllRead')}
                </button>
              )}
              <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <Bell className="w-8 h-8 text-gray-600 dark:text-gray-300 mx-auto mb-2" />
                <p className="text-gray-400 text-sm">{t('notif.noNotifications')}</p>
                <button
                  onClick={sendTestNotification}
                  className="mt-3 text-xs text-orange-400 hover:text-orange-300 underline"
                  data-testid="send-test-notification-btn"
                >
                  Send test notification
                </button>
              </div>
            ) : (
              notifications.map((n) => {
                const Icon = typeIcons[n.type] || Bell;
                const color = typeColors[n.type] || 'text-gray-400';
                return (
                  <div
                    key={n.id}
                    className={`px-4 py-3 border-b border-gray-800 hover:bg-gray-800/50 transition-colors ${!n.read ? 'bg-gray-800/30' : ''}`}
                    data-testid={`notification-item-${n.type}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`mt-0.5 ${color}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white">{n.title}</p>
                        <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">{n.body}</p>
                        {n.data?.suggestion && (
                          <div className="mt-1.5 bg-gray-800 rounded-md px-2.5 py-1.5 border border-gray-700/50">
                            <p className="text-xs text-orange-300 font-medium">{n.data.suggestion}</p>
                            {n.data.remaining_cal > 0 && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{n.data.remaining_cal} cal remaining of {n.data.calorie_goal}</p>
                            )}
                            {n.data.meal_name && !n._logged && (
                              <button
                                onClick={(e) => { e.stopPropagation(); quickLogMeal(n); }}
                                className="mt-1.5 w-full flex items-center justify-center gap-1.5 py-1.5 bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium rounded-md transition-colors"
                                data-testid={`quick-log-btn-${n.id}`}
                              >
                                <PlusCircle className="w-3.5 h-3.5" />
                                Quick Log: {n.data.meal_name} ({Math.round(n.data.meal_calories)} cal)
                              </button>
                            )}
                            {n._logged && (
                              <div className="mt-1.5 flex items-center justify-center gap-1.5 py-1.5 bg-green-600/20 text-green-400 text-xs font-medium rounded-md" data-testid="quick-log-success">
                                <Check className="w-3.5 h-3.5" /> Logged!
                              </div>
                            )}
                          </div>
                        )}
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{timeAgo(n.sent_at)}</p>
                      </div>
                      {!n.read && (
                        <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0" />
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {notifications.length > 0 && (
            <div className="px-4 py-2 border-t border-gray-700 text-center">
              <button
                onClick={sendTestNotification}
                className="text-xs text-gray-400 hover:text-orange-400 transition-colors"
                data-testid="send-test-notification-btn"
              >
                Send test notification
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
